import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getProductByEAN } from './canopyApiService';
import { getProductPriceByEAN, checkAmazonEligibility } from './amazonPriceService';
import { scrapeUrl } from './scraping';

interface Product {
  id: string;
  ean: string;
  name: string;
  regularPrice: number;
  stockQuantity: number;
}

interface ProductSubmission {
  id?: string;
  productId: string;
  ean: string;
  proposedPrice: number;
  desiredChannel: string;
  status: 'pending' | 'approved_primary' | 'approved_reserve' | 'rejected';
  stockQuantity?: number;
}

interface ValidationResult {
  isEligible: boolean;
  eligibilityType: 'primary' | 'reserve' | 'not_eligible';
  reason: string;
  suggestedPrice?: number;
  lastPriceInternal?: number;
  externalPrices?: Record<string, number | null>;
  realPrices?: Record<string, boolean>;
  approvalLogic?: string;
  competitorUrl?: string;
  commission?: number;
  commissionWithVAT?: number;
  valueToReceive?: number;
  ptPrice?: number;
  recommendedPrice?: number;
  priceWithoutVAT?: number;
  priceWithVAT?: number;
  commissionValue?: number;
  vatOnCommission?: number;
  totalCommission?: number;
  buyboxPrice?: number;
  totalOffers?: number;
}

interface EligibilityResult {
  isEligible: boolean;
  eligibilityType: 'primary' | 'reserve' | 'rejected';
  reason: string;
  suggestedPrice?: number;
  externalPrices?: Record<string, number | null>;
  realPrices?: Record<string, boolean>;
  approvalLogic?: string;
  buyboxPrice?: number;
  competitivePrice?: number;
  totalOffers?: number;
  commission?: number;
  commissionWithVAT?: number;
  valueToReceive?: number;
  recommendedPrice?: number;
}

interface Supplier {
  id: string;
  proposedPrice: number;
  status: 'approved_primary' | 'approved_reserve';
}

// Obtém preços externos de APIs reais
async function fetchExternalPrices(ean: string, channel: string): Promise<{ prices: Record<string, number | null>, realPrices: Record<string, boolean> }> {
  // Inicializar com valores nulos
  const prices: Record<string, number | null> = {
    amazon: null,
    worten: null,
    fnac: null,
    emtuacasa: null
  };
  
  // Rastrear quais preços são reais
  const realPrices: Record<string, boolean> = {
    amazon: false,
    worten: false,
    fnac: false,
    emtuacasa: false
  };
  
  try {
    // Buscar preço real da Amazon usando a webhook
    if (ean) {
      // Buscar preço na Amazon
      const amazonPrice = await getProductPriceByEAN(ean);
      
      if (amazonPrice) {
        prices.amazon = amazonPrice;
        realPrices.amazon = true;
        console.log(`Preço real da Amazon para EAN ${ean}: ${amazonPrice}`);
      }
    }
    
    // No futuro, podemos adicionar outras APIs para outros canais
    // Por enquanto, apenas a Amazon tem preços reais
    
    return { prices, realPrices };
  } catch (error) {
    console.error('Erro ao buscar preços externos:', error);
    return { 
      prices: {
        amazon: null,
        worten: null,
        fnac: null,
        emtuacasa: null
      },
      realPrices: {
        amazon: false,
        worten: false,
        fnac: false,
        emtuacasa: false
      }
    };
  }
}

// Verifica os fornecedores existentes para o mesmo EAN e canal
async function checkExistingSuppliers(ean: string, channel: string): Promise<Supplier[]> {
  const q = query(
    collection(db, 'marketplaceProductSubmissions'),
    where('ean', '==', ean),
    where('desiredChannel', '==', channel),
    where('status', 'in', ['approved_primary', 'approved_reserve'])
  );
  
  const querySnapshot = await getDocs(q);
  
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    proposedPrice: doc.data().proposedPrice,
    status: doc.data().status
  }));
}

// Obtém o último preço interno do produto
async function getLastInternalPrice(productId: string): Promise<number | null> {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);
    
    if (productSnap.exists()) {
      const productData = productSnap.data();
      return productData.regularPrice || productData.salePrice || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching internal price:', error);
    return null;
  }
}

/**
 * Verifica a elegibilidade de um produto para o marketplace da Amazon
 * @param product Produto a ser verificado
 * @param proposedPrice Preço proposto para o produto
 * @returns Objeto com informações de elegibilidade
 */
export async function checkAmazonMarketplaceEligibility(product: any, proposedPrice: number) {
  try {
    if (!product || !product.ean) {
      return {
        eligible: false,
        reason: 'Produto sem EAN válido'
      };
    }

    console.log(`Verificando elegibilidade na Amazon para produto ${product.name} (EAN: ${product.ean})`);
    
    // Buscar fornecedores existentes
    const existingSuppliers = await checkExistingSuppliers(product.ean, 'amazon');
    const primarySupplier = existingSuppliers.find(s => s.status === 'approved_primary');
    const reserveSupplier = existingSuppliers.find(s => s.status === 'approved_reserve');

    // Chamar o serviço de verificação da Amazon
    const amazonResult = await checkAmazonEligibility(product.ean, proposedPrice);
    console.log('Resultado da verificação na Amazon:', amazonResult);

    // Se tiver mais de 10 ofertas, rejeitar
    if (amazonResult.totalOffers > 10) {
      return {
        eligible: false,
        eligibilityType: 'rejected',
        reason: 'Produto com mais de 10 ofertas concorrentes',
        externalPrices: {
          amazon: amazonResult.buyboxPrice,
          amazonPT: amazonResult.ptPrice,
          amazonES: amazonResult.esPrice
        },
        totalOffers: amazonResult.totalOffers,
        buyboxPrice: amazonResult.buyboxPrice,
        recommendedPrice: amazonResult.recommendedPrice,
        commission: amazonResult.commission,
        commissionWithVAT: amazonResult.commissionWithVAT,
        valueToReceive: amazonResult.valueToReceive
      };
    }

    // Lógica de preços com base nos fornecedores existentes
    if (primarySupplier && reserveSupplier) {
      // Caso 1: Existe principal e reserva
      if (reserveSupplier.proposedPrice < primarySupplier.proposedPrice) {
        // Reserva tem preço menor - ele deve ser promovido
        if (proposedPrice < reserveSupplier.proposedPrice) {
          return {
            eligible: true,
            eligibilityType: 'reserve',
            reason: 'Preço menor que o atual reserva que será promovido a principal',
            suggestedPrice: reserveSupplier.proposedPrice,
            approvalLogic: 'O fornecedor reserva atual será promovido a principal devido ao menor preço',
            buyboxPrice: amazonResult.buyboxPrice,
            recommendedPrice: amazonResult.recommendedPrice,
            commission: amazonResult.commission,
            commissionWithVAT: amazonResult.commissionWithVAT,
            valueToReceive: amazonResult.valueToReceive
          };
        } else {
          return {
            eligible: false,
            eligibilityType: 'rejected',
            reason: `Preço proposto (${proposedPrice}€) maior que o preço do fornecedor reserva (${reserveSupplier.proposedPrice}€) que será promovido a principal`,
            suggestedPrice: reserveSupplier.proposedPrice,
            buyboxPrice: amazonResult.buyboxPrice,
            recommendedPrice: amazonResult.recommendedPrice,
            commission: amazonResult.commission,
            commissionWithVAT: amazonResult.commissionWithVAT,
            valueToReceive: amazonResult.valueToReceive
          };
        }
      } else {
        // Principal tem o menor preço
        if (proposedPrice < primarySupplier.proposedPrice) {
          return {
            eligible: true,
            eligibilityType: 'primary',
            reason: 'Preço menor que o atual principal',
            suggestedPrice: primarySupplier.proposedPrice,
            approvalLogic: 'O fornecedor atual será rebaixado para reserva e o atual reserva será removido',
            buyboxPrice: amazonResult.buyboxPrice,
            recommendedPrice: amazonResult.recommendedPrice,
            commission: amazonResult.commission,
            commissionWithVAT: amazonResult.commissionWithVAT,
            valueToReceive: amazonResult.valueToReceive
          };
        } else {
          return {
            eligible: false,
            eligibilityType: 'rejected',
            reason: `Preço proposto (${proposedPrice}€) maior que o preço do fornecedor principal (${primarySupplier.proposedPrice}€)`,
            suggestedPrice: primarySupplier.proposedPrice,
            buyboxPrice: amazonResult.buyboxPrice,
            recommendedPrice: amazonResult.recommendedPrice,
            commission: amazonResult.commission,
            commissionWithVAT: amazonResult.commissionWithVAT,
            valueToReceive: amazonResult.valueToReceive
          };
        }
      }
    } else if (primarySupplier) {
      // Caso 2: Existe apenas principal
      if (proposedPrice < primarySupplier.proposedPrice) {
        return {
          eligible: true,
          eligibilityType: 'primary',
          reason: 'Preço menor que o atual principal',
          suggestedPrice: primarySupplier.proposedPrice,
          approvalLogic: 'O fornecedor atual será rebaixado para reserva',
          buyboxPrice: amazonResult.buyboxPrice,
          recommendedPrice: amazonResult.recommendedPrice,
          commission: amazonResult.commission,
          commissionWithVAT: amazonResult.commissionWithVAT,
          valueToReceive: amazonResult.valueToReceive
        };
      } else if (proposedPrice === primarySupplier.proposedPrice) {
        return {
          eligible: true,
          eligibilityType: 'reserve',
          reason: 'Preço igual ao fornecedor principal',
          suggestedPrice: primarySupplier.proposedPrice,
          buyboxPrice: amazonResult.buyboxPrice,
          recommendedPrice: amazonResult.recommendedPrice,
          commission: amazonResult.commission,
          commissionWithVAT: amazonResult.commissionWithVAT,
          valueToReceive: amazonResult.valueToReceive
        };
      } else {
        return {
          eligible: false,
          eligibilityType: 'rejected',
          reason: `Preço proposto (${proposedPrice}€) maior que o preço do fornecedor principal (${primarySupplier.proposedPrice}€)`,
          suggestedPrice: primarySupplier.proposedPrice,
          buyboxPrice: amazonResult.buyboxPrice,
          recommendedPrice: amazonResult.recommendedPrice,
          commission: amazonResult.commission,
          commissionWithVAT: amazonResult.commissionWithVAT,
          valueToReceive: amazonResult.valueToReceive
        };
      }
    } else if (reserveSupplier) {
      // Caso 3: Existe apenas reserva
      if (proposedPrice <= reserveSupplier.proposedPrice) {
        return {
          eligible: true,
          eligibilityType: 'primary',
          reason: 'Preço menor ou igual ao atual reserva',
          suggestedPrice: reserveSupplier.proposedPrice,
          buyboxPrice: amazonResult.buyboxPrice,
          recommendedPrice: amazonResult.recommendedPrice,
          commission: amazonResult.commission,
          commissionWithVAT: amazonResult.commissionWithVAT,
          valueToReceive: amazonResult.valueToReceive
        };
      } else {
        return {
          eligible: false,
          eligibilityType: 'rejected',
          reason: `Preço proposto (${proposedPrice}€) maior que o preço do fornecedor reserva (${reserveSupplier.proposedPrice}€)`,
          suggestedPrice: reserveSupplier.proposedPrice,
          buyboxPrice: amazonResult.buyboxPrice,
          recommendedPrice: amazonResult.recommendedPrice,
          commission: amazonResult.commission,
          commissionWithVAT: amazonResult.commissionWithVAT,
          valueToReceive: amazonResult.valueToReceive
        };
      }
    } else {
      // Caso 4: Não existe nenhum fornecedor
      // Usar preço de Portugal ou buybox como referência
      const referencePrice = amazonResult.ptPrice || amazonResult.buyboxPrice;
      const suggestedPrice = referencePrice ? Math.min(referencePrice, proposedPrice) : proposedPrice;

      return {
        eligible: true,
        eligibilityType: 'primary',
        reason: 'Primeiro fornecedor para este produto',
        suggestedPrice,
        externalPrices: {
          amazon: amazonResult.buyboxPrice,
          amazonPT: amazonResult.ptPrice,
          amazonES: amazonResult.esPrice
        },
        totalOffers: amazonResult.totalOffers,
        buyboxPrice: amazonResult.buyboxPrice,
        recommendedPrice: amazonResult.recommendedPrice,
        commission: amazonResult.commission,
        commissionWithVAT: amazonResult.commissionWithVAT,
        valueToReceive: amazonResult.valueToReceive
      };
    }
  } catch (error) {
    console.error('Erro ao verificar elegibilidade na Amazon:', error);
    return {
      eligible: false,
      reason: 'Erro ao verificar elegibilidade na Amazon'
    };
  }
}

export async function checkWortenEligibility(
  proposedPrice: number,
  productUrl: string
): Promise<ValidationResult> {
  try {
    // Obtém o preço atual da Worten via scraping
    const wortenPrice = await scrapeUrl(productUrl);
    const wortenPriceNumber = parseFloat(wortenPrice);

    if (!wortenPriceNumber) {
      return {
        eligible: false,
        eligibilityType: 'not_eligible',
        reason: 'Não foi possível obter o preço atual na Worten',
        externalPrices: { worten: null },
        realPrices: { worten: false },
        buyboxPrice: null,
        totalOffers: 0
      };
    }

    // Constantes
    const VAT_RATE = 0.23; // 23% IVA
    const COMMISSION_RATE = 0.20; // 20% comissão
    const MIN_PRICE_DIFFERENCE = 0.05; // 5 cêntimos mínimo de diferença

    // Preço base e recomendado (igual à Worten)
    const basePrice = wortenPriceNumber;
    const recommendedPrice = basePrice;

    // Cálculos do preço proposto
    const priceWithoutVAT = proposedPrice / (1 + VAT_RATE);
    const priceWithVAT = proposedPrice;

    // Cálculos da comissão
    const commissionValue = priceWithoutVAT * COMMISSION_RATE;
    const vatOnCommission = commissionValue * VAT_RATE;
    const totalCommission = commissionValue + vatOnCommission;

    // Valor final que o vendedor receberá
    const valueToReceive = priceWithVAT - totalCommission;

    // Verifica se o preço proposto é competitivo
    // Deve estar pelo menos 0.05€ abaixo do preço recomendado
    const priceDifference = recommendedPrice - proposedPrice;
    const isEligible = priceDifference >= MIN_PRICE_DIFFERENCE;

    // Mensagem de elegibilidade
    let reason = '';
    if (isEligible) {
      // Se for elegível (pelo menos 0.05€ abaixo)
      reason = `O seu preço está ${priceDifference.toFixed(2)}€ abaixo do preço na Worten. Excelente potencial de vendas!`;
    } else if (proposedPrice > recommendedPrice) {
      // Se estiver acima do preço recomendado
      const difference = (proposedPrice - recommendedPrice).toFixed(2);
      reason = `O seu preço está ${difference}€ acima do preço na Worten. Sugerimos reduzir o preço para aumentar o potencial de vendas.`;
    } else {
      // Se estiver entre 0 e 0.05€ abaixo
      reason = `O seu preço deve estar pelo menos ${MIN_PRICE_DIFFERENCE.toFixed(2)}€ abaixo do preço na Worten para ser elegível.`;
    }

    return {
      eligible: isEligible,
      eligibilityType: isEligible ? 'primary' : 'not_eligible',
      reason,
      suggestedPrice: recommendedPrice,
      recommendedPrice,
      basePrice,
      externalPrices: { worten: wortenPriceNumber },
      realPrices: { worten: true },
      competitorUrl: productUrl,
      ptPrice: wortenPriceNumber,
      commission: COMMISSION_RATE * 100,
      commissionValue,
      vatOnCommission,
      totalCommission,
      valueToReceive,
      priceWithoutVAT,
      priceWithVAT,
      buyboxPrice: wortenPriceNumber, // Preço buybox é o preço da Worten
      totalOffers: 1 // Sempre 1 oferta
    };
  } catch (error) {
    console.error('Erro ao verificar elegibilidade na Worten:', error);
    return {
      eligible: false,
      eligibilityType: 'not_eligible',
      reason: 'Erro ao verificar o preço na Worten. Por favor, verifique se o URL está correto.',
      externalPrices: { worten: null },
      realPrices: { worten: false },
      buyboxPrice: null,
      totalOffers: 0
    };
  }
}

export async function checkKuantoKustaEligibility(
  proposedPrice: number,
  productUrl: string
): Promise<ValidationResult> {
  try {
    // Obtém o preço atual do KuantoKusta via scraping
    const kuantoKustaPrice = await scrapeUrl(productUrl);
    console.log('Preço obtido do KuantoKusta:', kuantoKustaPrice, typeof kuantoKustaPrice);
    
    // Garantir que o preço é convertido para número
    const kuantoKustaPriceNumber = parseFloat(kuantoKustaPrice);
    console.log('Preço convertido para número:', kuantoKustaPriceNumber);

    if (!kuantoKustaPriceNumber || isNaN(kuantoKustaPriceNumber)) {
      return {
        eligible: false,
        eligibilityType: 'not_eligible',
        reason: 'Não foi possível obter o preço atual no KuantoKusta',
        externalPrices: { kuantokusta: null },
        realPrices: { kuantokusta: false },
        buyboxPrice: null,
        totalOffers: 0
      };
    }

    // Constantes
    const VAT_RATE = 0.23; // 23% IVA
    const COMMISSION_RATE = 0.20; // 20% comissão
    const MIN_PRICE_DIFFERENCE = 0.05; // 5 cêntimos mínimo de diferença

    // Preço base e recomendado (igual ao KuantoKusta)
    const basePrice = kuantoKustaPriceNumber;
    const recommendedPrice = basePrice;

    // Cálculos do preço proposto
    const priceWithoutVAT = proposedPrice / (1 + VAT_RATE);
    const priceWithVAT = proposedPrice;

    // Cálculos da comissão
    const commissionValue = priceWithoutVAT * COMMISSION_RATE;
    const vatOnCommission = commissionValue * VAT_RATE;
    const totalCommission = commissionValue + vatOnCommission;

    // Valor final que o vendedor receberá
    const valueToReceive = priceWithVAT - totalCommission;

    // Verifica se o preço proposto é competitivo
    // Deve estar pelo menos 0.05€ abaixo do preço recomendado
    const priceDifference = recommendedPrice - proposedPrice;
    const isEligible = priceDifference >= MIN_PRICE_DIFFERENCE;

    // Mensagem de elegibilidade
    let reason = '';
    if (isEligible) {
      reason = `O seu preço está ${priceDifference.toFixed(2)}€ abaixo do preço no KuantoKusta. Excelente potencial de vendas!`;
    } else if (proposedPrice > recommendedPrice) {
      const difference = (proposedPrice - recommendedPrice).toFixed(2);
      reason = `O seu preço está ${difference}€ acima do preço no KuantoKusta. Sugerimos reduzir o preço para aumentar o potencial de vendas.`;
    } else {
      reason = `O seu preço deve estar pelo menos ${MIN_PRICE_DIFFERENCE.toFixed(2)}€ abaixo do preço no KuantoKusta para ser elegível.`;
    }

    return {
      eligible: isEligible,
      eligibilityType: isEligible ? 'primary' : 'not_eligible',
      reason,
      suggestedPrice: recommendedPrice,
      recommendedPrice,
      basePrice,
      externalPrices: { kuantokusta: kuantoKustaPriceNumber },
      realPrices: { kuantokusta: true },
      competitorUrl: productUrl,
      ptPrice: kuantoKustaPriceNumber,
      commission: COMMISSION_RATE * 100,
      commissionValue,
      vatOnCommission,
      totalCommission,
      valueToReceive,
      priceWithoutVAT,
      priceWithVAT,
      buyboxPrice: kuantoKustaPriceNumber,
      totalOffers: 1
    };
  } catch (error) {
    console.error('Erro ao verificar elegibilidade no KuantoKusta:', error);
    return {
      eligible: false,
      eligibilityType: 'not_eligible',
      reason: 'Erro ao verificar o preço no KuantoKusta. Por favor, verifique se o URL está correto.',
      externalPrices: { kuantokusta: null },
      realPrices: { kuantokusta: false },
      buyboxPrice: null,
      totalOffers: 0
    };
  }
}

/**
 * Verifica a elegibilidade de um produto para a FNAC
 * @param proposedPrice Preço proposto para o produto
 * @param productUrl URL do produto na FNAC
 * @returns Resultado da validação
 */
export async function checkFnacEligibility(
  proposedPrice: number,
  productUrl: string
): Promise<ValidationResult> {
  try {
    const basePrice = await scrapeUrl(productUrl);
    const recommendedPrice = parseFloat(basePrice) - 0.05;
    
    // Calcula comissão base (23%)
    const commissionValue = (proposedPrice / 1.23) * 0.23;
    
    // Calcula IVA sobre a comissão (23%)
    const vatOnCommission = commissionValue * 0.23;
    
    // Calcula valor que o vendedor receberá
    const valueToReceive = proposedPrice - commissionValue - vatOnCommission;
    
    // Verifica elegibilidade
    const isEligible = proposedPrice <= recommendedPrice;
    
    return {
      eligible: isEligible,
      reason: isEligible 
        ? 'Preço aceite para fornecedor principal'
        : 'O preço proposto deve ser menor que o preço recomendado',
      basePrice: parseFloat(basePrice),
      recommendedPrice,
      commissionValue,
      vatOnCommission,
      valueToReceive,
      competitorUrl: productUrl
    };
  } catch (error) {
    console.error('Erro ao verificar elegibilidade na FNAC:', error);
    throw error;
  }
}

/**
 * Valida a elegibilidade de um produto para um canal específico
 * @param product Produto a ser validado
 * @param proposedPrice Preço proposto para o produto
 * @param channel Canal para o qual o produto será submetido
 * @returns Objeto com informações de elegibilidade
 */
export async function validateProductEligibility(
  product: any,
  proposedPrice: number,
  channel: string,
  productUrl?: string
): Promise<ValidationResult> {
  if (!channel) {
    console.error('Canal não especificado');
    return {
      eligible: false,
      reason: 'Canal não especificado'
    };
  }

  console.log(`Validando elegibilidade para ${channel}:`, { product, proposedPrice });
  
  try {
    switch (channel.toLowerCase()) {
      case 'amazon':
        return checkAmazonMarketplaceEligibility(product, proposedPrice);
      case 'worten':
        if (!productUrl) throw new Error('URL do produto na Worten é obrigatória');
        return checkWortenEligibility(proposedPrice, productUrl);
      case 'kuantokusta':
        if (!productUrl) throw new Error('URL do produto no KuantoKusta é obrigatória');
        return checkKuantoKustaEligibility(proposedPrice, productUrl);
      case 'fnac':
        if (!productUrl) throw new Error('URL do produto na FNAC é obrigatória');
        return checkFnacEligibility(proposedPrice, productUrl);
      default:
        throw new Error('Canal não suportado: ' + channel);
    }
  } catch (error) {
    console.error('Erro ao validar elegibilidade:', error);
    throw error;
  }
}

// Função auxiliar para formatar moeda
function formatCurrency(value: number): string {
  return `${value.toFixed(2).replace('.', ',')}€`;
}
